import { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { ALL_ENTITY_TYPES, type EntityType } from '@/api/search';
import { ENTITY_META } from './entityMeta';
import { cn } from '@/lib/cn';

export interface TypeFilterDropdownProps {
  value: EntityType[];   // empty = all
  onChange: (v: EntityType[]) => void;
}

export function TypeFilterDropdown({ value, onChange }: TypeFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const allSelected = value.length === 0;
  const label = allSelected
    ? 'All types'
    : value.length === 1
      ? ENTITY_META[value[0]!].label
      : `${value.length} types`;

  function toggle(t: EntityType) {
    if (value.includes(t)) onChange(value.filter((x) => x !== t));
    else onChange([...value, t]);
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 shadow-sm hover:bg-slate-50"
      >
        <span>{label}</span>
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-20 mt-1 max-h-80 w-56 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg">
            <button
              type="button"
              onClick={() => { onChange([]); setOpen(false); }}
              className="flex w-full items-center justify-between px-3 py-1.5 text-xs hover:bg-slate-50"
            >
              <span className={cn(allSelected && 'font-semibold')}>All types</span>
              {allSelected && <Check className="h-3 w-3 text-blue-500" />}
            </button>
            <div className="my-1 border-t border-slate-100" />
            {ALL_ENTITY_TYPES.map((t) => {
              const Icon = ENTITY_META[t].icon;
              const checked = value.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggle(t)}
                  className="flex w-full items-center justify-between px-3 py-1.5 text-xs hover:bg-slate-50"
                >
                  <span className="flex items-center gap-1.5">
                    <Icon className="h-3 w-3 text-slate-400" />
                    {ENTITY_META[t].label}
                  </span>
                  {checked && <Check className="h-3 w-3 text-blue-500" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
